import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { Button, Card, Heading, Text, Box, Flex } from 'theme-ui';
import { MdWarning, MdCheckCircle, MdErrorOutline } from 'react-icons/md';
import { formatOSTA } from '../ostaCalculator/ostaCalculator';
import { parseFraxValue, safeParseFloat, safeParseInt } from '../utils/errorHandler';

const Results = ({ calculateOSTA, fraxResults, formData, setStep, setFormData, hasVisitedMainMenu, onRiskUpdate, hospitalInfo, handleResubmit, onBack, toggleVideo, resetFormData, submitFormData }) => {
    const [risk, setRisk] = useState(null);
    const [buttonText, setButtonText] = useState('最初の画面にもどる');
    const [explanationButtonText, setExplanationButtonText] = useState('説明動画を見る(1分)');
    const [processingError, setProcessingError] = useState(false);
    const [retryButtonText, setRetryButtonText] = useState('再送信');
    const [retryCount, setRetryCount] = useState(0);
    const [retryProcessing, setRetryProcessing] = useState(false);
    const [dataSubmitted, setDataSubmitted] = useState(false);
    const [riskCalculated, setRiskCalculated] = useState(false);

    // コンポーネントがマウントされた時点でlastPageを設定
    useEffect(() => {
        // lastPageとナビゲーション情報を更新
        sessionStorage.setItem('lastPage', 'Results');
        sessionStorage.setItem('has_navigated_to_results', 'true');
        
        // デバッグ情報
        console.log('Results: コンポーネントマウント時のデータ:', {
            formData,
            fraxResults: {
                majorOsteoporotic: fraxResults.majorOsteoporotic,
                hipFracture: fraxResults.hipFracture,
                calculating: fraxResults.calculating,
                error: fraxResults.error
            }
        });
    }, []);

    useEffect(() => {
        // fraxResultsのエラーチェック
        if (fraxResults.majorOsteoporotic === 'エラー' || fraxResults.hipFracture === 'エラー') {
            console.log('Results: FRAXデータにエラーがあります。エラー画面を表示します。');
            setProcessingError(true);
            return;
        }

        // 結果が利用可能で、まだ計算していない場合のみ処理する
        if (
            fraxResults.majorOsteoporotic && 
            fraxResults.hipFracture && 
            !riskCalculated
        ) {
            console.log('Results: 有効なFRAX結果を検出。リスク計算を実行します:', {
                majorOsteoporotic: fraxResults.majorOsteoporotic,
                hipFracture: fraxResults.hipFracture,
                riskCalculated
            });
            calculateAndSetRisk();
        } else {
            console.log('Results: FRAX結果が不完全か、すでに計算済みです:', {
                majorOsteoporotic: fraxResults.majorOsteoporotic || 'データなし',
                hipFracture: fraxResults.hipFracture || 'データなし',
                riskCalculated
            });
        }
    }, [fraxResults, riskCalculated]);

    // バックグラウンドでデータを再送信する関数
    const retrySubmitDataInBackground = (calculatedRisk, osta, fraxMajor, fraxHip) => {
        try {
            // データ送信の失敗情報をローカルストレージに保存
            const sessionId = sessionStorage.getItem('session_id');
            if (!sessionId) {
                console.error('セッションIDがないため保存できません');
                return;
            }
            
            // 保存するデータを構築
            const storageData = {
                session_id: sessionId,
                complete: true,
                risk: calculatedRisk,
                osta_value: osta,
                frax_major: fraxMajor,
                frax_hip: fraxHip,
                timestamp: new Date().toISOString(),
                original_timestamp: new Date().toISOString(), // 元の実行時間を保存
                gender: formData.gender || 'female',
                age: formData.age || '65',
                height: formData.height || '',
                weight: formData.weight || '',
                dental_check: formData.dentalCheck,
                dental_issue: formData.dentalIssue,
                previous_fracture: formData.previousFracture,
                parent_hip_fracture: formData.parentHipFracture,
                current_smoking: formData.currentSmoking,
                alcohol: formData.alcohol,
                glucocorticoids: formData.glucocorticoids,
                rheumatoid_arthritis: formData.rheumatoidArthritis,
                secondary_osteoporosis: formData.secondaryOsteoporosis,
                osteoporosis_treatment: formData.osteoporosisTreatment,
                bone_density_test: formData.boneDensityTest
            };
            
            // ローカルストレージに保存
            const storageKey = `pending_results_data_${Date.now()}`;
            localStorage.setItem(storageKey, JSON.stringify(storageData));
            console.log('未送信データをローカルストレージに保存しました:', storageKey);
            
            // すぐに1回再試行 (ユーザーがまだページを見ている可能性がある場合)
            setTimeout(() => {
                try {
                    console.log('即時再試行を実行します');
                    onRiskUpdate(calculatedRisk, osta, fraxMajor, fraxHip);
                    // 成功したらローカルストレージから削除
                    localStorage.removeItem(storageKey);
                    console.log('即時再試行が成功し、一時データを削除しました:', storageKey);
                } catch (error) {
                    console.error('即時再試行エラー:', error);
                    // エラーの場合は次回アプリ起動時に処理される
                }
            }, 3000);
        } catch (error) {
            console.error('ローカルストレージへの保存エラー:', error);
        }
    };

    // リスク計算と設定を行う関数
    const calculateAndSetRisk = () => {
        try {
            // 値の安全な取得とパース
            let osta, fraxMajor, fraxHip, age, gender, previousFracture;
            
            try {
                // formDataから年齢と体重を取得して正しくcalculateOSTAに渡す
                const weightValue = safeParseFloat(formData.weight, 55);
                const ageValue = safeParseFloat(formData.age, 65);
                
                console.log('OSTA計算のための入力値:', { age: ageValue, weight: weightValue, formData });
                
                // calculateOSTA関数に正しいパラメータを渡す（数値を返す）
                osta = calculateOSTA(weightValue, ageValue);
                console.log('OSTA計算結果:', osta, '表示用:', formatOSTA(osta));
            } catch (error) {
                console.error('OSTA計算エラー:', error, formData);
                // 年齢と体重から安全に推定
                const safeAge = safeParseInt(formData.age, 65);
                const safeWeight = safeParseFloat(formData.weight, 55);
                osta = (0.2 * (safeWeight - safeAge));
                console.log('OSTA計算エラー後のフォールバック値:', osta, '表示用:', formatOSTA(osta));
            }
            
            try {
                // 共通関数を使用してFRAX値をパース（デフォルト値10）
                fraxMajor = parseFraxValue(fraxResults.majorOsteoporotic, 10);
                console.log('Frax Major 解析結果:', fraxMajor);
            } catch (error) {
                console.error('FRAX Major パースエラー:', error, fraxResults.majorOsteoporotic);
                fraxMajor = 10; // フォールバック値
            }
            
            try {
                // 共通関数を使用してFRAX値をパース（デフォルト値5）
                fraxHip = parseFraxValue(fraxResults.hipFracture, 5);
                console.log('Frax Hip 解析結果:', fraxHip);
            } catch (error) {
                console.error('FRAX Hip パースエラー:', error, fraxResults.hipFracture);
                fraxHip = 5; // フォールバック値
            }
            
            // 他の値も安全に取得
            age = safeParseInt(formData.age, 65); // デフォルト65歳
            gender = formData.gender || 'female'; // デフォルト女性
            previousFracture = !!formData.previousFracture; // 確実にブール値に
            
            console.log('リスク計算のための入力値:', {
                age,
                gender,
                osta,
                fraxMajor,
                fraxHip,
                previousFracture
            });

            const determineRisk = () => {
                let risk = null;

                try {
                    // 各条件判定では数値をそのまま使用（parseFloatは不要）
                    // ostaとfraxMajorは既に数値型
                    
                    console.log(`リスク判定中: 年齢=${age}, 性別=${gender}, OSTA=${osta}, FRAX=${fraxMajor}`);
                    
                    if (age <= 64 && gender === 'male') {
                        risk = 'blue';
                        console.log('64歳以下の男性: 青信号判定');
                    } else if (age <= 64 && gender === 'female') {
                        if (osta <= -1 && fraxMajor >= 5) {
                            risk = 'red';
                            console.log('64歳以下の女性: OSTA≦-1 かつ FRAX≧5% で赤信号判定');
                        } else if (osta <= -1 || fraxMajor >= 5) {
                            risk = 'yellow';
                            console.log('64歳以下の女性: OSTA≦-1 または FRAX≧5% で黄信号判定');
                        } else {
                            risk = 'blue';
                            console.log('64歳以下の女性: 基準未満のため青信号判定');
                        }
                    } else if (age >= 65 && age <= 74 && gender === 'male') {
                        if (osta <= -1 && fraxMajor >= 5) {
                            risk = 'red';
                            console.log('65-74歳の男性: OSTA≦-1 かつ FRAX≧5% で赤信号判定');
                        } else if (osta <= -1 || fraxMajor >= 5) {
                            risk = 'yellow';
                            console.log('65-74歳の男性: OSTA≦-1 または FRAX≧5% で黄信号判定');
                        } else {
                            risk = 'blue';
                            console.log('65-74歳の男性: 基準未満のため青信号判定');
                        }
                    } else if (age >= 65 && age <= 74 && gender === 'female') {
                        if (osta <= -1 && fraxMajor >= 10) {
                            risk = 'red';
                            console.log('65-74歳の女性: OSTA≦-1 かつ FRAX≧10% で赤信号判定');
                        } else if (osta <= -1 || fraxMajor >= 10) {
                            risk = 'yellow';
                            console.log('65-74歳の女性: OSTA≦-1 または FRAX≧10% で黄信号判定');
                        } else {
                            risk = 'blue';
                            console.log('65-74歳の女性: 基準未満のため青信号判定');
                        }
                    } else if (age >= 75) {
                        if (osta < -4 && fraxMajor >= 10) {
                            risk = 'redred';
                            console.log('75歳以上: OSTA＜-4 かつ FRAX≧10% でダブル赤信号判定');
                        } else if (osta < -4 || fraxMajor >= 10) {
                            risk = 'red';
                            console.log('75歳以上: OSTA＜-4 または FRAX≧10% で赤信号判定');
                        } else {
                            risk = 'yellow';
                            console.log('75歳以上: それ以外の場合は黄信号判定');
                        }
                    }

                    if (previousFracture) {
                        const prevRisk = risk;
                        if (risk === 'redred') {
                            risk = 'redred';
                        } else if (risk === 'red') {
                            risk = 'redred';
                        } else {
                            risk = 'red';
                        }
                        console.log(`骨折歴あり: リスクを上昇 ${prevRisk} -> ${risk}`);
                    }
                    
                    console.log('リスク判定結果:', risk);
                } catch (error) {
                    console.error('リスク判定エラー:', error, { age, gender, osta, fraxMajor });
                    // エラーの場合は安全側に倒したリスク評価を返す
                    risk = 'yellow'; // デフォルトで黄色（要指導）
                }

                // リスクが設定されなかった場合のフォールバック
                if (!risk) {
                    console.warn('リスクが設定されませんでした。デフォルト値を使用します。');
                    risk = 'yellow'; // デフォルトで黄色（要指導）
                }

                return risk;
            };

            const calculatedRisk = determineRisk();
            console.log('最終的なリスク判定:', calculatedRisk);
            
            // リスク計算が完了したことをマーク
            setRiskCalculated(true);
            
            // UIに反映
            setRisk(calculatedRisk);
            
            try {
                // リスク更新と同時にSupabaseにデータを送信
                console.log('Supabaseにデータ送信開始:', {
                    risk: calculatedRisk,
                    osta,
                    fraxMajor,
                    fraxHip
                });
                // 表示用の値ではなく計算用の数値型を引き渡す
                onRiskUpdate(calculatedRisk, osta, fraxMajor, fraxHip);
                
                // データ送信が完了したとみなし、セッション終了フラグを設定
                if (!dataSubmitted) {
                    console.log('リスク計算・データ送信完了：セッション終了');
                    sessionStorage.setItem('session_completed', 'true');
                    setDataSubmitted(true);
                }
            } catch (updateError) {
                console.error('リスク更新エラー:', updateError);
                // エラーがあっても表示は継続、バックグラウンドで再送信を試みる
                retrySubmitDataInBackground(calculatedRisk, osta, fraxMajor, fraxHip);
            }
        } catch (error) {
            console.error('結果処理中の重大なエラー:', error, {
                formData,
                fraxResults
            });
            // エラーの場合でも何らかの結果を表示する
            setRisk('yellow'); // デフォルトで黄色（要指導）
            setRiskCalculated(true); // 計算完了とマーク（再計算しないように）
            
            // フォームデータが最低限あれば登録を試みる
            if (formData && formData.age && formData.gender) {
                try {
                    const safeOsta = 0; // 安全なデフォルト値
                    const safeFraxMajor = 10; // 安全なデフォルト値
                    const safeFraxHip = 5; // 安全なデフォルト値
                    console.log('エラー後のフォールバック値でデータ送信:', {
                        risk: 'yellow',
                        osta: safeOsta,
                        fraxMajor: safeFraxMajor,
                        fraxHip: safeFraxHip
                    });
                    
                    try {
                        onRiskUpdate('yellow', safeOsta, safeFraxMajor, safeFraxHip);
                    } catch (submitError) {
                        console.error('エラー後のリスク更新エラー:', submitError);
                        // バックグラウンドで再送信を試みる
                        retrySubmitDataInBackground('yellow', safeOsta, safeFraxMajor, safeFraxHip);
                    }
                    
                    // エラー時でもデータ送信が完了したとみなし、セッション終了フラグを設定
                    if (!dataSubmitted) {
                        console.log('エラー後のリスク送信完了：セッション終了');
                        sessionStorage.setItem('session_completed', 'true');
                        setDataSubmitted(true);
                    }
                } catch (e) {
                    console.error('エラー後のリスク更新エラー:', e);
                    // バックグラウンドで再送信を試みる
                    const safeOsta = 0;
                    const safeFraxMajor = 10;
                    const safeFraxHip = 5;
                    retrySubmitDataInBackground('yellow', safeOsta, safeFraxMajor, safeFraxHip);
                }
            }
            
            // エラーを画面に表示しない（UXを優先）
            setProcessingError(false);
        }
    };

    const getRiskMessage = () => {
        const highlightedText = (text, color) => (
            <span style={{ backgroundColor: color, color: 'black', padding: '0 4px', fontWeight: '900', fontSize: 'calc(1.5rem * var(--dynamic-font-size, 1))' }}>{text}</span>
        );
    
        switch (risk) {
            case 'blue':
                return (
                    <>
                        <div className="font-bold text-lg mb-2">{highlightedText('あなたは 青信号(異常なし) です。', 'skyblue')}</div>
                        <p className="font-bold text-lg mb-2">骨粗鬆症のリスクは低いですが、日々の栄養・運動に気をつけましょう。</p>
                    </>
                );
            case 'yellow':
                return (
                    <>
                        <div className="font-bold text-lg mb-2">{highlightedText('あなたは 黄信号(要指導) です。', 'yellow')}</div>
                        <p className="font-bold text-lg mb-2">栄養指導・運動指導を行います。医療機関での骨密度測定をご希望の方はお申し出ください。</p>
                    </>
                );
            case 'red':
                return (
                    <>
                        <div className="font-bold text-lg mb-2">{highlightedText('あなたは 赤信号(要精査①) です。', 'pink')}</div>
                        <p className="font-bold text-lg mb-2">骨粗しょう症のリスクが高いので、医療機関での骨密度測定を推奨します。</p>
                    </>
                );
            case 'redred':
                return (
                    <>
                        <div className="font-bold text-lg mb-2">{highlightedText('あなたは ダブル赤信号(要精査②) です。', 'pink')}</div>
                        <p className="font-bold text-lg mb-2">骨粗しょう症のリスクが非常に高いので、医療機関での骨密度測定を強く推奨します。</p>
                    </>
                );
            default:
                return null;
        }
    };

    const getMessageForHost = () => {
        const host = window.location.hostname;
        if (host.endsWith('-hosp.vercel.app') || host.startsWith('honenavi.app') || host.startsWith('www.honenavi.app')) {
            return (
                <p className="font-bold text-lg text-red-600 mb-2">
                    <span className="underline-wavy">この画面を開いたまま</span>、危険度チェックが完了した旨をスタッフにお声がけください。<br />
                </p>
            );
        } else if (host.endsWith('-home.vercel.app')) {
            return (
                <p className="font-bold text-lg text-red-600 mb-2">
                    <span className="underline-wavy">この結果は次回来院時に必要です。</span><br />
                    メモまたはスクリーンショットを撮影し、忘れないようにしてください。
                </p>
            );
        }
        return null;
    };

    const getDentalMessage = () => {
        if (!formData.dentalCheck || formData.dentalIssue) {
            return (
                <p className="font-bold text-lg text-red-600 mb-2">
                    また、<span className="underline-wavy">歯科検査</span>を受けて、お口の中の状態を確認しておきましょう。
                </p>
            );
        }
        return null;
    };

    const handleRetry = async () => {
        setRetryProcessing(true);
        setRetryButtonText('処理中...');
        
        try {
            // 前回のフォームデータが保存されていれば使用
            const dataToSubmit = fraxResults.lastFormData || formData;
            if (!dataToSubmit) {
                throw new Error('再送信に必要なデータがありません');
            }
            
            // フォームデータを更新
            setFormData(dataToSubmit);
            
            // Step10に戻って再計算を実行
            setProcessingError(false);
            setStep(11); // Step10に戻る
        } catch (error) {
            console.error('Retry preparation failed:', error);
            setRetryButtonText('再送信');
        } finally {
            setRetryProcessing(false);
        }
    };

    const handleBackToStart = () => {
        setButtonText('処理中...');
        // セッションIDと保存されたフォームデータをクリア
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('currentStep');
        // フォームデータをリセット
        resetFormData(true);
        onBack();
    };

    if (processingError) {
        return (
            <Card sx={{ textAlign: 'center', p: 4, maxWidth: '600px', mx: 'auto', my: 4 }}>
                <MdErrorOutline size={64} color="red" style={{ marginBottom: '1rem' }} />
                <Heading as="h2" sx={{ fontSize: 4, mb: 3 }}>計算でエラーが発生しました</Heading>
                <Text sx={{ fontSize: 3, mb: 4 }}>
                    {fraxResults.statusMessage || 'データの処理中にエラーが発生しました。再送信を試すか、最初からやり直してください。'}
                </Text>
                <Flex sx={{ flexDirection: ['column', 'row'], justifyContent: 'center', gap: 3 }}>
                    <Button 
                        onClick={handleRetry} 
                        disabled={retryProcessing || retryCount >= 3}
                        sx={{ 
                            bg: retryCount >= 3 ? 'gray' : 'primary',
                            flex: 1
                        }}
                    >
                        {retryButtonText} {retryCount > 0 ? `(${retryCount}/3)` : ''}
                    </Button>
                    <Button 
                        onClick={handleBackToStart} 
                        disabled={buttonText !== '最初の画面にもどる'}
                        sx={{ 
                            bg: 'secondary',
                            flex: 1
                        }}
                    >
                        {buttonText}
                    </Button>
                </Flex>
            </Card>
        );
    }

    return (
        <div className="p-4 bg-red-100 rounded-lg">
            <h2 className="text-xl font-bold mb-4 dynamic-font">結果</h2>
            <div className="flex justify-center mb-4">
                <div className="relative bg-gray-200 p-2 rounded-lg flex space-x-2 items-center justify-center">
                    <div className={`circle ${risk === 'blue' ? 'bg-blue-500' : (risk === 'redred' ? 'bg-red-500' : 'bg-gray-300')} shadow`}>
                        <div className="light"></div>
                    </div>
                    <div className={`circle ${risk === 'yellow' ? 'bg-yellow-500' : (risk === 'redred' ? 'bg-red-500' : 'bg-gray-300')} shadow`}>
                        <div className="light"></div>
                    </div>
                    <div className={`circle ${risk === 'red' || risk === 'redred' ? 'bg-red-500' : 'bg-gray-300'} shadow`}>
                        <div className="light"></div>
                    </div>
                </div>
            </div>
            <div className="flex justify-center">
                <div className="flex flex-col items-center text-center space-y-2">
                    {getRiskMessage()}
                    {getDentalMessage()}
                </div>
            </div>
            <br />
            <div className="flex justify-center">
                {getMessageForHost()}
            </div>
            <br />
            <div className="flex items-center justify-between mt-4">
                <button
                    onClick={() => {
                        // リトライフラグをリセット
                        sessionStorage.removeItem('has_navigated_to_results');
                        // 新たなセッション開始のためにセッションIDをクリア
                        sessionStorage.removeItem('session_id');
                        
                        const initialStep = hospitalInfo && hospitalInfo.targetting === false ? 2 : 0;
                        setStep(initialStep);
                        setFormData({
                            weight: '',
                            age: '',
                            gender: '',
                            height: '',
                            previousFracture: false,
                            parentHipFracture: false,
                            currentSmoking: false,
                            glucocorticoids: false,
                            rheumatoidArthritis: false,
                            secondaryOsteoporosis: false,
                            alcohol: false,
                            dentalCheck: null,
                            dentalIssue: null,
                            osteoporosisTreatment: null,
                            boneDensityTest: null,
                            isSubmitted: false // isSubmittedをリセット
                        });
                        setRisk(null);
                        console.log('セッションを終了し、新しいセッションの準備ができました');
                    }} 
                    className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                >
                    最初の画面へ戻る
                </button>

                <button
                    onClick={() => setStep(13)}
                    className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
                >
                    検査の説明を見る(1分)
                </button>
            </div>
        </div>
    );
};

export default Results;